// Generic Delete Modal component for bulk delete operations
import { useTranslation } from 'react-i18next';
import Accordion from '@common/components/Accordion/Accordion';
import Spinner from '@common/components/Spinner';
import Modal from '@common/components/Modal/Modal';
import {
  CloseCircleIcon,
  CheckmarkCircleIcon,
  Delete2Icon,
} from '@common/components/icons';
import * as styles from './DeleteModal.module.css';

export default function DeleteModal({
  isOpen,
  deleteStatus,
  deleteResults,
  deleteError,
  openAccordion,
  setOpenAccordion,
  selectedCount,
  onSubmit,
  onClose,
  translationPrefix,
  getItemName = (item) => item.name,
  getItemVersion = (item) => item.version,
}) {
  const { t } = useTranslation();

  const renderTitle = () => (
    <span className={styles.deleteTitle}>
      <Delete2Icon className={styles.deleteIcon} />
      {t(`${translationPrefix}.title`)}
    </span>
  );

  const getIdleContent = () => ({
    title: renderTitle(),
    subtitle: t(`${translationPrefix}.subtitle`),
    body: (
      <div>
        <div>
          {selectedCount}{' '}
          {selectedCount === 1
            ? t(`${translationPrefix}.itemLabelSingular`)
            : t(`${translationPrefix}.itemLabelPlural`)}{' '}
          {t(`${translationPrefix}.selectedText`)}
        </div>
      </div>
    ),
    submitText: t(`${translationPrefix}.submitButton`),
  });

  const getLoadingContent = () => ({
    title: renderTitle(),
    subtitle: t(`${translationPrefix}.subtitle`),
    body: (
      <div className={styles.loadingContainer}>
        <Spinner />
        <p className={styles.loadingText}>
          {t(`${translationPrefix}.loadingText`)}
        </p>
      </div>
    ),
    hideFooter: true,
    isLoading: true,
  });

  const getErrorContent = () => ({
    title: renderTitle(),
    subtitle: t(`${translationPrefix}.subtitle`),
    body: (
      <div className={styles.errorContent}>
        <CloseCircleIcon className={styles.failedIcon} />
        <span className={styles.errorText}>
          {t(`${translationPrefix}.errorTitle`)}{' '}
          {deleteError ? ` - ${deleteError}` : ''}
        </span>
      </div>
    ),
    submitText: t(`${translationPrefix}.submitButton`),
    submitDisabled: true,
  });

  const getResultsContent = () => {
    const renderSuccessItem = (item, index) => (
      <div key={index}>
        <span className={`${styles.boldText} ${styles.itemNumber}`}>
          {index + 1}.{' '}
        </span>
        <span className={styles.boldText}>
          {getItemName(item)}
          {getItemVersion(item) ? ` v${getItemVersion(item)}` : ''} -{' '}
        </span>
        <span>{t(`${translationPrefix}.successfullyDeleted`)}</span>
      </div>
    );

    const renderFailedItem = (item, index) => (
      <div key={index}>
        <span className={`${styles.boldText} ${styles.itemNumber}`}>
          {index + 1}.{' '}
        </span>
        <span className={styles.boldText}>
          {getItemName(item)}
          {getItemVersion(item) ? ` v${getItemVersion(item)}` : ''} -{' '}
        </span>
        <span>{item.error || t(`${translationPrefix}.failedToDelete`)}</span>
      </div>
    );

    return {
      title: renderTitle(),
      subtitle: t(`${translationPrefix}.subtitle`),
      body: (
        <div>
          <div>
            {deleteResults?.success && deleteResults.success.length > 0 && (
              <Accordion
                icon={<CheckmarkCircleIcon className={styles.successIcon} />}
                title={t(`${translationPrefix}.successTitle`, {
                  count: deleteResults.success.length,
                })}
                items={deleteResults.success}
                isOpen={openAccordion === 'success'}
                onToggle={() =>
                  setOpenAccordion(openAccordion === 'success' ? null : 'success')
                }
                renderItem={renderSuccessItem}
              />
            )}
            {deleteResults?.failed && deleteResults.failed.length > 0 && (
              <Accordion
                icon={<CloseCircleIcon className={styles.failedIcon} />}
                title={t(`${translationPrefix}.failedTitle`, {
                  count: deleteResults.failed.length,
                })}
                items={deleteResults.failed}
                isOpen={openAccordion === 'failed'}
                onToggle={() =>
                  setOpenAccordion(openAccordion === 'failed' ? null : 'failed')
                }
                renderItem={renderFailedItem}
              />
            )}
          </div>
        </div>
      ),
      submitText: t(`${translationPrefix}.submitButton`),
      submitDisabled: true,
    };
  };

  const getContent = () => {
    if (deleteStatus === 'loading') {
      return getLoadingContent();
    }
    if (deleteStatus === 'error') {
      return getErrorContent();
    }
    if (deleteStatus === 'results' && deleteResults) {
      return getResultsContent();
    }
    return getIdleContent();
  };

  return (
    <Modal
      {...getContent()}
      submitAction={onSubmit}
      submitVariant="danger"
      showCloseButton={false}
      closeOnOverlayClick={false}
      closeOnEscape={false}
      onClose={onClose}
      isOpen={isOpen}
    />
  );
}

